import React, { useEffect, useState, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import {
  CreditCard, Plus, Search, TrendingUp,
  AlertCircle, Eye, Receipt, MoreHorizontal, Download, SlidersHorizontal, Trash2, RotateCcw, Image as ImageIcon,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import "./fees-collection.scss";
import { useNavigate } from "react-router-dom";
import { useBranch } from "../../contexts/BranchContext";
import FilePreviewModal from "../Common/FilePreviewModal";
import {
  PageHeader, AppButton, AppCard, AppCardContent,
  StatsCard, UserAvatar, EmptyState, FilterModal, FilterGroup,
  Paginator, ActionMenu, ActionMenuItem, ActionMenuDivider,
} from "../ui";
import ConfirmModal from "../ui/ConfirmModal";
import RefundModal from "../ui/RefundModal";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

const FeesCollectionPage = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranch();

  const [filters, setFilters] = useState({
    name: "",
    status: "",
    date: "",
  });

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalUrl, setModalUrl] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalFileName, setModalFileName] = useState("");
  const [invoiceDownloading, setInvoiceDownloading] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState("");

  const activeFilterCount = [filters.status, filters.date].filter(Boolean).length;

  const handleResetFilters = () => {
    setFilters((f) => ({ ...f, status: "", date: "" }));
    setShowFilterModal(false);
  };

  const [page, setPage] = useState(1);
  const ENTRIES_PER_PAGE = 10;

  useEffect(() => {
    setLoading(true);
    apiClient
      .get("/fees/all")
      .then(({ data }) => setTransactions(data.transactions || []))
      .catch(() => setTransactions([]))
      .finally(() => setLoading(false));
  }, [selectedBranchId]);

  function getStatus(tr) {
    if (tr.paymentStatus === "refunded") return { key: "refunded", label: "Refunded" };
    if (tr.paymentStatus === "partially_refunded") return { key: "partial-refund", label: "Refunded" };
    if (
      (Number(tr.studentId?.pendingFees) === 0 ||
        Number(tr.studentId?.totalFees) === Number(tr.studentId?.paidFees)) ||
      tr.pending === 0
    )
      return { key: "active", label: "Paid" };
    if (Number(tr.studentId?.paidFees) > 0 && Number(tr.studentId?.pendingFees) > 0)
      return { key: "pending", label: "Partial" };
    return { key: "inactive", label: "Pending" };
  }

  // Client-side filtering
  const filtered = useMemo(() => {
    return transactions.filter((tr) => {
      const studentName = tr.studentId?.name?.toLowerCase() || "";
      const status = getStatus(tr).label.toLowerCase();
      return (
        (filters.name === "" || studentName.includes(filters.name.toLowerCase())) &&
        (filters.status === "" || status === filters.status.toLowerCase()) &&
        (filters.date === "" || (tr.date && tr.date.slice(0, 10) === filters.date))
      );
    });
  }, [transactions, filters]);

  const totalPages = Math.ceil(filtered.length / ENTRIES_PER_PAGE);
  const paginated = filtered.slice(
    (page - 1) * ENTRIES_PER_PAGE,
    page * ENTRIES_PER_PAGE
  );

  // Stats
  const stats = useMemo(() => {
    const unique = new Map();
    transactions.forEach((tr) => {
      if (tr.studentId?._id && !unique.has(tr.studentId._id)) {
        unique.set(tr.studentId._id, tr.studentId);
      }
    });
    const students = Array.from(unique.values());
    const totalCollected = students.reduce((s, st) => s + Number(st.paidFees || 0), 0);
    const totalPending = students.reduce((s, st) => s + Number(st.pendingFees || 0), 0);
    const totalFees = students.reduce((s, st) => s + Number(st.totalFees || 0), 0);
    const pendingCount = students.filter((st) => Number(st.pendingFees) > 0).length;
    const rate = totalFees > 0 ? Math.round((totalCollected / totalFees) * 100) : 0;
    const totalRefunded = transactions
      .filter((tr) => tr.paymentStatus === "refunded" || tr.paymentStatus === "partially_refunded")
      .reduce((s, tr) => s + Number(tr.refundAmount || tr.currentPayment || 0), 0);
    const refundedCount = transactions.filter((tr) => tr.paymentStatus === "refunded" || tr.paymentStatus === "partially_refunded").length;
    return { totalCollected, totalPending, totalFees, pendingCount, rate, totalRefunded, refundedCount };
  }, [transactions]);

  const handleViewImage = async (s3Key, fileName) => {
    setModalLoading(true);
    setShowModal(true);
    setModalFileName(fileName || "Screenshot");
    try {
      const { data } = await apiClient.get(`/files/presign-get?key=${encodeURIComponent(s3Key)}`);
      setModalUrl(data.url);
    } catch {
      setModalUrl(null);
    }
    setModalLoading(false);
  };

  const handleInvoiceDownload = async (feeId) => {
    setInvoiceDownloading(feeId);
    try {
      const res = await apiClient.get(`/fees/${feeId}/invoice`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `invoice-${feeId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // silent fail — could add toast if desired
    }
    setInvoiceDownloading(null);
  };

  const handleDeleteFee = async () => {
    if (!deleteTarget || !deleteReason.trim()) return;
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/fees/${deleteTarget._id}`, { data: { reason: deleteReason.trim() } });
      setTransactions((prev) => prev.filter((t) => t._id !== deleteTarget._id));
      setDeleteTarget(null);
      setDeleteReason("");
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to delete fee transaction.");
    }
    setDeleteLoading(false);
  };

  const handleRefundFee = async (reason, amount, revokeAccess) => {
    if (!refundTarget) return;
    setRefundLoading(true);
    setRefundError("");
    try {
      const isOnline = refundTarget.paymentGateway === "razorpay";
      const endpoint = isOnline
        ? `/payments/${refundTarget._id}/refund`
        : `/fees/${refundTarget._id}/refund`;
      await apiClient.post(endpoint, { reason, amount, revokeAccess });
      // Update local state — mark as refunded
      setTransactions((prev) =>
        prev.map((t) =>
          t._id === refundTarget._id
            ? { ...t, paymentStatus: amount < (t.currentPayment - (t.refundAmount || 0)) ? "partially_refunded" : "refunded", refundAmount: (t.refundAmount || 0) + amount }
            : t
        )
      );
      setRefundTarget(null);
    } catch (err) {
      setRefundError(err?.response?.data?.message || "Failed to refund fee transaction.");
    }
    setRefundLoading(false);
  };

  return (
    <div className="fees-page">
      <PageHeader
        title="Fees Management"
        description="Track and manage student fee payments"
        icon={CreditCard}
      />

      {/* ── Mobile compact summary (visible < 768px) ── */}
      <div className="fees-mobile-summary">
        <div className="fees-mobile-summary__row">
          <div className="fees-mobile-summary__item fees-mobile-summary__item--emerald">
            <span className="fees-mobile-summary__label">Collected</span>
            <span className="fees-mobile-summary__value">{formatCurrency(stats.totalCollected)}</span>
          </div>
          <div className="fees-mobile-summary__divider" />
          <div className="fees-mobile-summary__item fees-mobile-summary__item--amber">
            <span className="fees-mobile-summary__label">Pending</span>
            <span className="fees-mobile-summary__value">{formatCurrency(stats.totalPending)}</span>
          </div>
          <div className="fees-mobile-summary__divider" />
          <div className="fees-mobile-summary__item fees-mobile-summary__item--blue">
            <span className="fees-mobile-summary__label">Total</span>
            <span className="fees-mobile-summary__value">{formatCurrency(stats.totalFees)}</span>
          </div>
          {stats.totalRefunded > 0 && (
            <>
              <div className="fees-mobile-summary__divider" />
              <div className="fees-mobile-summary__item fees-mobile-summary__item--rose">
                <span className="fees-mobile-summary__label">Refunded</span>
                <span className="fees-mobile-summary__value">{formatCurrency(stats.totalRefunded)}</span>
              </div>
            </>
          )}
        </div>
        <div className="fees-mobile-summary__progress">
          <div className="fees-mobile-summary__progress-header">
            <span>Collection Rate</span>
            <span className="fees-mobile-summary__progress-pct">{stats.rate}%</span>
          </div>
          <div className="fees-mobile-summary__progress-bar">
            <div
              className="fees-mobile-summary__progress-fill"
              style={{ width: `${stats.rate}%` }}
            />
          </div>
        </div>
      </div>

      {/* ── Desktop stats cards (hidden < 768px) ── */}
      <div className="fees-stats">
        <StatsCard
          title="Total Collected"
          value={formatCurrency(stats.totalCollected)}
          subtitle="All time collections"
          icon={TrendingUp}
          iconColor="emerald"
          gradient="emerald"
        />
        <StatsCard
          title="Pending Fees"
          value={formatCurrency(stats.totalPending)}
          subtitle={`${stats.pendingCount} students pending`}
          icon={AlertCircle}
          iconColor="amber"
          gradient="amber"
        />
        <StatsCard
          title="Total Fees"
          value={formatCurrency(stats.totalFees)}
          subtitle="Expected revenue"
          icon={CreditCard}
          iconColor="blue"
          gradient="blue"
        />
        {stats.totalRefunded > 0 && (
          <StatsCard
            title="Refunded"
            value={formatCurrency(stats.totalRefunded)}
            subtitle={`${stats.refundedCount} transaction${stats.refundedCount !== 1 ? "s" : ""}`}
            icon={RotateCcw}
            iconColor="rose"
            gradient="rose"
          />
        )}
        {/* Semicircle Gauge Card */}
        <div className="fees-gauge-card hover-elevate">
          <svg className="fees-gauge-card__wave" viewBox="0 0 300 100" preserveAspectRatio="none">
            <path d="M0,56 C50,40 100,60 150,46 C200,30 250,52 300,38 L300,100 L0,100 Z" fill="rgba(139,92,246,0.07)" />
            <path d="M0,48 C50,34 105,54 155,40 C205,26 255,46 300,32" fill="none" stroke="rgba(139,92,246,0.14)" strokeWidth="1.5" />
            <path d="M0,40 C55,28 108,46 160,34 C210,20 260,38 300,26" fill="none" stroke="rgba(139,92,246,0.07)" strokeWidth="1" />
          </svg>
          <div className="fees-gauge">
            <svg className="fees-gauge__svg" viewBox="0 0 120 70">
              <path
                className="fees-gauge__track"
                d="M10,65 A50,50 0 0,1 110,65"
              />
              <path
                className="fees-gauge__fill"
                d="M10,65 A50,50 0 0,1 110,65"
                strokeDasharray={Math.PI * 50}
                strokeDashoffset={Math.PI * 50 * (1 - stats.rate / 100)}
              />
            </svg>
            <span className="fees-gauge__value">{stats.rate}%</span>
          </div>
          <p className="fees-gauge__title">Collection Rate</p>
          <p className="fees-gauge__subtitle">Overall progress</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="fees-toolbar">
        <AppButton className="fees-add-btn" onClick={() => navigate("/fees/new")}>
          <Plus size={16} />
          <span className="fees-add-btn-text">Collect Fee</span>
        </AppButton>

        <div className="fees-search">
          <Search size={16} className="fees-search__icon" />
          <input
            type="text"
            className="fees-search__input"
            placeholder="Search students..."
            value={filters.name}
            onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
          />
        </div>

        <div className="fees-toolbar-actions">
          {/* Desktop inline filters */}
          <select
            className="fees-filter-select d-none d-md-block"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">All Status</option>
            <option value="Paid">Paid</option>
            <option value="Partial">Partial</option>
            <option value="Pending">Pending</option>
            <option value="Refunded">Refunded</option>
          </select>
          <input
            type="date"
            className="fees-filter-input d-none d-md-block"
            value={filters.date}
            onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))}
          />

          {/* Mobile filter button */}
          <button className="fees-filter-btn d-md-none" onClick={() => setShowFilterModal(true)}>
            <SlidersHorizontal size={16} />
            {activeFilterCount > 0 && <span className="fees-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onReset={handleResetFilters}
      >
        <FilterGroup label="Status">
          <select
            className="form-select"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <option value="">All Status</option>
            <option value="Paid">Paid</option>
            <option value="Partial">Partial</option>
            <option value="Pending">Pending</option>
            <option value="Refunded">Refunded</option>
          </select>
        </FilterGroup>
        <FilterGroup label="Date">
          <input
            type="date"
            className="form-control"
            value={filters.date}
            onChange={(e) => setFilters((f) => ({ ...f, date: e.target.value }))}
          />
        </FilterGroup>
      </FilterModal>

      {/* Table */}
      <AppCard className="fees-table-card">
        <AppCardContent className="p-0">
          {loading ? (
            <div className="fees-loading">
              <Spinner animation="border" size="sm" />
              <span>Loading fee records...</span>
            </div>
          ) : paginated.length === 0 ? (
            <EmptyState
              icon={<CreditCard size={40} />}
              title="No fee records found"
              description="Try adjusting your search or filter criteria"
            />
          ) : (
            <div className="fees-table-wrapper">
              <table className="fees-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Course</th>
                    <th>Amount</th>
                    <th>Mode</th>
                    <th>Txn ID</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((tr) => {
                    const isOnline = tr.paymentGateway === "razorpay";
                    const txnDate = tr.date || tr.createdAt;
                    return (
                      <tr key={tr._id}>
                        <td>
                          <div
                            className="fees-cell-user"
                            onClick={() => tr.studentId?._id && navigate(`/students/${tr.studentId._id}`)}
                          >
                            <UserAvatar name={tr.studentId?.name || "?"} size="sm" />
                            <div className="fees-cell-user__details">
                              <span className="fees-cell-user__name">{tr.studentId?.name}</span>
                              {tr.studentId?.email && (
                                <span className="fees-cell-user__email">{tr.studentId.email}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="fees-cell-course">
                            <span className="fees-cell-course__name">
                              {tr.courseAssignmentId?.courseId?.name || tr.studentId?.course?.name || "-"}
                            </span>
                            {(tr.courseAssignmentId?.batchId?.name || tr.studentId?.batch?.name) && (
                              <span className="fees-cell-course__batch">
                                {tr.courseAssignmentId?.batchId?.name || tr.studentId?.batch?.name}
                              </span>
                            )}
                            {tr.courseAssignmentId?.courseId?.courseType === 'self-paced' && (
                              <span className="fees-cell-course__tag">Self-paced</span>
                            )}
                          </div>
                        </td>
                        <td className="fees-cell-paid">{formatCurrency(tr.currentPayment)}</td>
                        <td>
                          <span className={`fees-mode-badge${isOnline ? " fees-mode-badge--online" : ""}`}>
                            {tr.paymentMode || "—"}
                          </span>
                          {tr.paymentType === "lms_payment" && (
                            <span className="fees-mode-badge fees-mode-badge--online" style={{ marginLeft: 4, fontSize: "0.65rem" }}>LMS</span>
                          )}
                        </td>
                        <td>
                          <span className="fees-cell-txn">
                            {tr.referenceNumber || tr.razorpayPaymentId || "—"}
                          </span>
                        </td>
                        <td className="fees-cell-date">
                          {txnDate ? new Date(txnDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit", timeZone: "Asia/Kolkata" }) : "—"}
                        </td>
                        <td>
                          {(() => {
                            const status = tr.paymentStatus || "success";
                            const isOnline = tr.paymentGateway === "razorpay";
                            if (status === "refunded") {
                              return <span className="fees-status-badge fees-status-badge--refunded">Refunded</span>;
                            }
                            if (status === "partially_refunded") {
                              return <span className="fees-status-badge fees-status-badge--partial-refund">Partial Refund</span>;
                            }
                            if (isOnline) {
                              return (
                                <span className={`fees-status-badge fees-status-badge--${status}`}>
                                  {status === "success" ? "Paid" : status}
                                </span>
                              );
                            }
                            return <span className="fees-status-badge fees-status-badge--success">Paid</span>;
                          })()}
                        </td>
                        <td className="fees-cell-actions">
                          <ActionMenu trigger={<MoreHorizontal size={16} />}>
                            <ActionMenuItem onClick={() => navigate(
                              tr.studentId?._id
                                ? `/students/${tr.studentId._id}/fees/view/${tr._id}`
                                : `/fees/${tr._id}/details`
                            )}>
                              <Eye size={14} /> View Details
                            </ActionMenuItem>
                            {tr.paymentStatus !== "refunded" && (
                              <ActionMenuItem onClick={() => handleInvoiceDownload(tr._id)}>
                                <Download size={14} /> {invoiceDownloading === tr._id ? "Downloading…" : "Download Invoice"}
                              </ActionMenuItem>
                            )}
                            {tr.refImage && (
                              <ActionMenuItem onClick={() => handleViewImage(tr.refImage)}>
                                <ImageIcon size={14} /> View Payment Proof
                              </ActionMenuItem>
                            )}
                            <ActionMenuItem onClick={() => tr.studentId?._id && navigate(`/students/${tr.studentId._id}/fees/new`)}>
                              <Receipt size={14} /> Collect Fee
                            </ActionMenuItem>
                            {(tr.paymentStatus === "success" || tr.paymentStatus === "partially_refunded") && (
                              <ActionMenuItem onClick={() => setRefundTarget(tr)}>
                                <RotateCcw size={14} /> Refund
                              </ActionMenuItem>
                            )}
                            {tr.paymentStatus !== "refunded" && (
                              <>
                                <ActionMenuDivider />
                                <ActionMenuItem danger onClick={() => { setDeleteTarget(tr); setDeleteReason(""); }}>
                                  <Trash2 size={14} /> Delete
                                </ActionMenuItem>
                              </>
                            )}
                          </ActionMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <Paginator
              page={page}
              totalPages={totalPages}
              total={filtered.length}
              pageSize={ENTRIES_PER_PAGE}
              onChange={setPage}
            />
          )}
        </AppCardContent>
      </AppCard>

      <FilePreviewModal
        show={showModal}
        onHide={() => { setShowModal(false); setModalUrl(null); }}
        url={modalUrl}
        fileName={modalFileName}
        loading={modalLoading}
        isImage={true}
      />

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => { if (!deleteLoading) { setDeleteTarget(null); setDeleteReason(""); } }}
        onConfirm={handleDeleteFee}
        title="Delete Fee Transaction"
        message={
          <div className="fees-delete-confirm">
            <p>
              Are you sure you want to delete the fee of{" "}
              <strong>{formatCurrency(deleteTarget?.currentPayment)}</strong> for{" "}
              <strong>{deleteTarget?.studentId?.name || "this student"}</strong>?
            </p>
            <p className="fees-delete-confirm__warn">This action cannot be undone. The student's paid/pending balance will be recalculated.</p>
            <label className="fees-delete-confirm__label">Reason for deletion <span>*</span></label>
            <textarea
              className="fees-delete-confirm__input"
              rows={3}
              placeholder="Enter reason for deleting this fee..."
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              maxLength={500}
            />
          </div>
        }
        confirmText="Delete Fee"
        variant="danger"
        loading={deleteLoading}
        confirmDisabled={!deleteReason.trim()}
      />

      <RefundModal
        show={!!refundTarget}
        onHide={() => { if (!refundLoading) { setRefundTarget(null); setRefundError(""); } }}
        onConfirm={handleRefundFee}
        error={refundError}
        title="Refund Fee Transaction"
        studentName={refundTarget?.studentId?.name || ""}
        originalAmount={refundTarget?.currentPayment || 0}
        maxRefundable={(refundTarget?.currentPayment || 0) - (refundTarget?.refundAmount || 0)}
        isOnline={refundTarget?.paymentGateway === "razorpay"}
        loading={refundLoading}
        courseName={refundTarget?.courseAssignmentId?.courseId?.name || refundTarget?.studentId?.course?.name || ""}
        courseType={refundTarget?.courseAssignmentId?.courseId?.courseType || null}
      />
    </div>
  );
};

export default FeesCollectionPage;
